import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

// ================================================================= //
//          Accessible Label Component for Form Inputs               //
// ================================================================= //
// This component is built on top of Radix UI's accessible Label     //
// primitive. It ensures that clicking the label correctly focuses   //
// the associated input element, which is crucial for accessibility. //
// ================================================================= //

// --- Label Variants using CVA ---
// Defines the base styling for the label. While simple, using CVA
// maintains consistency with other UI components in the library.
const labelVariants = cva(
  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground'
);

// --- TypeScript Props Interface ---
// Extends the props from the Radix UI Label primitive and adds our
// CVA variant props.
export interface LabelProps
  extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    VariantProps<typeof labelVariants> {}

// --- Label Component Implementation ---
// Uses React.forwardRef to allow parent components to pass a ref to the
// underlying DOM element.
const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  />
));

// Set a display name for easier debugging in React DevTools.
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
